import { Card, CardContent, CardHeader, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getAgents } from "redux/selectors/getAgents";
import { useGetAutomations } from "hooks/useGetAutomations";
import ChannelConnectionsAdminService from "services/admin/channelConnections";
import PageGuide from "components/PageGuide/PageGuide";
import { BotVsAgentTable } from "components/ReplyModes/ReplyModes";

interface Props {
  channelConnection: any;
  refetch: () => void;
}

/**
 * «Кто отвечает в этом канале»: менеджер → сценарии чат-бота → ИИ-агент.
 * Порядок совпадает с логикой бэкенда (decision/decision-engine.ts).
 */
export const ChannelResponders = ({ channelConnection, refetch }: Props) => {
  const agents = ((useSelector(getAgents) as any[]) || []) as any[];
  const { data: automations } = useGetAutomations();

  const isSandbox = channelConnection.source === "TEST_SANDBOX";
  const channelAutomations = (automations || []).filter(
    (a: any) => a.channelConnectionID === channelConnection.id && a.status !== "DELETED"
  );
  const activeAutomations = channelAutomations.filter((a: any) => a.status === "ACTIVE");
  // Сценарии чат-бота сейчас работают только в Instagram
  const chatbotSupported = channelConnection.source === "INSTAGRAM";
  const agent = agents.find((a: any) => a.id === channelConnection.agentID);

  const changeAgent = async (agentID: number | "") => {
    try {
      await ChannelConnectionsAdminService.put(channelConnection.id, { agentID: agentID === "" ? null : agentID });
      toast.success(agentID === "" ? "ИИ-агент отключён от канала" : "ИИ-агент подключён к каналу");
      refetch();
    } catch (error) {
      toast.error("Не удалось изменить ИИ-агента");
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader title="Кто отвечает в этом канале" subheader="По порядку: кто первый подходит — тот и отвечает" />
      <CardContent>
        <ol className="flex flex-col gap-4">
          <li>
            <div className="font-medium">1. 👤 Менеджер</div>
            <div className="text-sm text-gray-600">
              Всегда может ответить во «Входящих». Если менеджер ответил в чате, ИИ-агент в этом чате замолкает.
            </div>
          </li>

          {!isSandbox && (
            <li>
              <div className="font-medium">2. 🤖 Чат-бот (сценарии)</div>
              <div className="text-sm text-gray-600">
                {!chatbotSupported ? (
                  "Сценарии чат-бота пока работают только в Instagram."
                ) : activeAutomations.length > 0 ? (
                  <>
                    Активных сценариев: <b>{activeAutomations.length}</b>. Если сообщение подходит под сценарий
                    (ключевое слово, кнопка), отвечает чат-бот.{" "}
                    <Link to="/admin/automations" className="text-blue-600">Открыть сценарии</Link>
                  </>
                ) : (
                  <>
                    Сценариев нет.{" "}
                    <Link to="/admin/automations" className="text-blue-600">Создать сценарий</Link> — например,
                    прайс в директ по слову «цена».
                  </>
                )}
              </div>
            </li>
          )}

          <li>
            <div className="font-medium">{isSandbox ? "2" : "3"}. 🧠 ИИ-агент</div>
            <div className="text-sm text-gray-600 mb-2">
              {agent
                ? `Отвечает «${agent.name}» на все сообщения, которые не подошли под сценарии чат-бота.`
                : "Не подключён: на сообщения, которые не подошли под сценарии, ответит только менеджер."}
            </div>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="channel-agent-label">ИИ-агент</InputLabel>
              <Select
                labelId="channel-agent-label"
                label="ИИ-агент"
                value={channelConnection.agentID || ""}
                onChange={(e) => changeAgent(e.target.value as number | "")}
              >
                <MenuItem value="">
                  <em>Без ИИ-агента</em>
                </MenuItem>
                {agents.map((a: any) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </li>
        </ol>

        {!isSandbox && (
          <PageGuide id="bot-vs-agent-channel" title="Чем отличаются чат-бот и ИИ-агент?" className="mt-4 mb-0">
            <BotVsAgentTable />
          </PageGuide>
        )}
      </CardContent>
    </Card>
  );
};

export default ChannelResponders;
